import React, { useEffect, useRef, useState } from 'react';
import BugReportIcon from '../icons/BugReportIcon';
import FeaturesIcon from '../icons/FeaturesIcon';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning' | 'bug-closed' | 'feature-closed';
  duration?: number;
  onClick?: () => void;
  onClose: () => void;
  onEasterEgg?: () => void;
  index?: number;
  copyPayload?: string;
  persistent?: boolean;
}

export const Toast: React.FC<ToastProps> = ({ message, type = 'success', duration = 5000, onClick, onClose, onEasterEgg, index = 0, copyPayload, persistent = false }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const easterEggFiredRef = useRef(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show popover on mount so it renders in the top layer (above dialogs/drawers)
  useEffect(() => {
    const el = popoverRef.current;
    if (el && typeof el.showPopover === 'function') {
      try { el.showPopover(); } catch { /* already showing */ }
    }
    return () => {
      if (el && typeof el.hidePopover === 'function') {
        try { el.hidePopover(); } catch { /* already hidden */ }
      }
    };
  }, []);

  useEffect(() => {
    if (persistent || duration === 0) return; // Don't auto-close if persistent or duration is 0

    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose, persistent]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!copyPayload) return;
    try {
      await navigator.clipboard.writeText(copyPayload);
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write can fail (insecure context, permission denied). Fail silently.
    }
  };

  const bgColors = {
    success: 'bg-green-500 dark:bg-green-600',
    error: 'bg-red-500 dark:bg-red-600',
    info: 'bg-blue-500 dark:bg-blue-600',
    warning: 'bg-yellow-500 dark:bg-yellow-600',
    'bug-closed': 'bg-purple-500 dark:bg-purple-600',
    'feature-closed': 'bg-purple-500 dark:bg-purple-600',
  };

  const icons = {
    success: (
      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    error: (
      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    info: (
      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    'bug-closed': (
      <BugReportIcon className="h-5 w-5 text-white" />
    ),
    'feature-closed': (
      <FeaturesIcon className="h-5 w-5 text-white" />
    ),
  };

  return (
    <div
      ref={popoverRef}
      popover="manual"
      style={{ top: `${1 + index * 4.5}rem` }}
      className="fixed right-4 z-[9999] animate-slide-in m-0 p-0 bg-transparent border-none inset-auto transition-all duration-300"
    >
      <div
        className={`${bgColors[type]} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-[300px] max-w-md ${onClick ? 'cursor-pointer hover:opacity-90' : ''}`}
        onClick={onClick ? () => { onClick(); } : undefined}
        onMouseEnter={onEasterEgg ? () => {
          if (easterEggFiredRef.current) return;
          hoverTimerRef.current = setTimeout(() => {
            easterEggFiredRef.current = true;
            onEasterEgg();
          }, 15000);
        } : undefined}
        onMouseLeave={onEasterEgg ? () => {
          if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
          }
        } : undefined}
      >
        <div className="flex-shrink-0">
          {icons[type]}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        {copyPayload && (
          <button
            onClick={handleCopy}
            className="flex-shrink-0 ml-2 text-white hover:text-gray-200 focus:outline-none flex items-center gap-1"
            title="Copy diagnostic details to clipboard"
            aria-label="Copy diagnostic details to clipboard"
          >
            {copied ? (
              <span className="text-xs font-semibold">Copied!</span>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
              </svg>
            )}
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="flex-shrink-0 ml-2 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
